/**
 * Frame Preview SVG Generator
 * Ported 1:1 from designer-studio/app/utils/frame-preview.ts
 *
 * Pure function: takes a frameSpec object, returns an SVG markup string.
 * No DOM, no framework, no side effects.
 *
 * @param {FrameSpec} frameSpec
 * @returns {string} SVG markup string
 *
 * FrameSpec shape:
 * {
 *   imageUrl: string,
 *   frameConfig: { productId: string, frameImageLR: string, frameImageTB?: string, frameWidth: number },
 *   matConfig: { type: 'none'|'standard'|'inner', width: 'thin'|'standard'|'wide'|'weighted'|'individual',
 *                color: string, colorImageUrl?: string,
 *                individualSizes?: { top: number, right: number, bottom: number, left: number } },
 *   dimensions: { width: number, height: number },
 *   rotation?: number
 * }
 */
function generateFramePreviewSvg(frameSpec) {
  var dimensions = frameSpec.dimensions;
  var frameConfig = frameSpec.frameConfig;
  var matConfig = frameSpec.matConfig;
  var imageUrl = frameSpec.imageUrl;
  var rotation = frameSpec.rotation;

  var pixelsPerCm = 15;
  var frameImageLR = escapeXml(frameConfig.frameImageLR || '');
  var frameImageTB = escapeXml(frameConfig.frameImageTB || frameConfig.frameImageLR || '');
  var customerImageUrl = escapeXml(imageUrl || '');
  var matTextureUrl = matConfig.colorImageUrl ? escapeXml(matConfig.colorImageUrl) : null;

  var imageWidth = Math.max(1, dimensions.width) * pixelsPerCm;
  var imageHeight = Math.max(1, dimensions.height) * pixelsPerCm;

  var frameThickness = Math.max(0, frameConfig.frameWidth) / 10 * pixelsPerCm;

  var presetMatWidthsCm = {
    thin: 3.5,
    standard: 5.5,
    wide: 8.5,
    weighted: 5,
    individual: 5
  };

  var matTop = 0;
  var matBottom = 0;
  var matLeft = 0;
  var matRight = 0;

  if (matConfig.type !== 'none') {
    if (matConfig.width === 'individual' && matConfig.individualSizes) {
      var sizes = matConfig.individualSizes;
      matTop = Math.max(0, sizes.top || 0) * pixelsPerCm;
      matBottom = Math.max(0, sizes.bottom || 0) * pixelsPerCm;
      matLeft = Math.max(0, sizes.left || 0) * pixelsPerCm;
      matRight = Math.max(0, sizes.right || 0) * pixelsPerCm;
    } else {
      var baseWidthCm = presetMatWidthsCm[matConfig.width] != null
        ? presetMatWidthsCm[matConfig.width]
        : presetMatWidthsCm.standard;
      var baseWidthPx = baseWidthCm * pixelsPerCm;
      matTop = baseWidthPx;
      matBottom = baseWidthPx;
      matLeft = baseWidthPx;
      matRight = baseWidthPx;
    }
  }

  var totalWidth = imageWidth + matLeft + matRight + 2 * frameThickness;
  var totalHeight = imageHeight + matTop + matBottom + 2 * frameThickness;

  var imageX = frameThickness + matLeft;
  var imageY = frameThickness + matTop;

  var matOuterWidth = totalWidth - 2 * frameThickness;
  var matOuterHeight = totalHeight - 2 * frameThickness;

  var uniqueSuffix = 'fp-' + Date.now().toString(36) + Math.floor(Math.random() * 1e6).toString(36);
  var frameTopPatternId = 'frame-top-' + uniqueSuffix;
  var frameRightPatternId = 'frame-right-' + uniqueSuffix;
  var frameBottomPatternId = 'frame-bottom-' + uniqueSuffix;
  var frameLeftPatternId = 'frame-left-' + uniqueSuffix;
  var maskRightId = 'frame-mask-right-' + uniqueSuffix;
  var maskBottomId = 'frame-mask-bottom-' + uniqueSuffix;
  var maskLeftId = 'frame-mask-left-' + uniqueSuffix;
  var matPatternId = 'frame-mat-' + uniqueSuffix;

  var matPattern = '';
  if (matConfig.type !== 'none') {
    if (matTextureUrl) {
      matPattern = '<pattern id="' + matPatternId + '" patternUnits="userSpaceOnUse" width="40" height="40">' +
        '<image href="' + matTextureUrl + '" x="0" y="0" width="40" height="40" preserveAspectRatio="xMidYMid slice" />' +
        '</pattern>';
    } else {
      matPattern = '<pattern id="' + matPatternId + '" patternUnits="userSpaceOnUse" width="2" height="2">' +
        '<rect width="2" height="2" fill="#f8f8f8"/>' +
        '<rect x="0" y="0" width="1" height="1" fill="#ffffff"/>' +
        '<rect x="1" y="1" width="1" height="1" fill="#ffffff"/>' +
        '</pattern>';
    }
  }

  var matFill = matConfig.type === 'none' ? 'none' : 'url(#' + matPatternId + ')';

  var matPath = '';
  if (matConfig.type !== 'none') {
    var strokeW = Math.max(1, frameThickness * 0.04);

    if (matConfig.type === 'inner') {
      // Freigestelltes Passepartout: PP-Linie 0.5cm vom Bild entfernt
      // Von innen nach außen: Bild → 0.5cm PP → PP-Linie → restliches PP
      var innerInsetPx = 0.5 * pixelsPerCm; // 0.5cm innerer PP-Bereich

      // Gesamte PP-Fläche (äußeres Rect minus Bild-Cutout) – ohne Stroke am Bildrand
      matPath = '<path d="M ' + frameThickness + ',' + frameThickness +
        ' h ' + matOuterWidth + ' v ' + matOuterHeight + ' h ' + (-matOuterWidth) + ' Z' +
        ' M ' + imageX + ',' + imageY + ' v ' + imageHeight + ' h ' + imageWidth + ' v ' + (-imageHeight) + ' Z"' +
        ' fill="' + matFill + '" fill-rule="evenodd"/>';

      // PP-Linie als separates Rechteck, 0.5cm vom Bildrand entfernt
      var ppLineX = imageX - innerInsetPx;
      var ppLineY = imageY - innerInsetPx;
      var ppLineW = imageWidth + 2 * innerInsetPx;
      var ppLineH = imageHeight + 2 * innerInsetPx;
      var ppLineStrokeW = Math.max(1.5, frameThickness * 0.06);
      matPath += '<rect x="' + ppLineX + '" y="' + ppLineY + '" width="' + ppLineW + '" height="' + ppLineH +
        '" fill="none" stroke="#bbb" stroke-width="' + ppLineStrokeW + '"/>';
    } else {
      // Einfaches Passepartout: PP-Linie direkt am Bildrand
      matPath = '<path d="M ' + frameThickness + ',' + frameThickness +
        ' h ' + matOuterWidth + ' v ' + matOuterHeight + ' h ' + (-matOuterWidth) + ' Z' +
        ' M ' + imageX + ',' + imageY + ' v ' + imageHeight + ' h ' + imageWidth + ' v ' + (-imageHeight) + ' Z"' +
        ' fill="' + matFill + '" fill-rule="evenodd" stroke="#ddd" stroke-width="' + strokeW + '"/>';
    }
  }

  var rotationAttr = '';
  if (rotation) {
    rotationAttr = ' transform="rotate(' + rotation + ' ' + (imageX + imageWidth / 2) + ' ' + (imageY + imageHeight / 2) + ')"';
  }

  return '<svg width="' + totalWidth + '" height="' + totalHeight + '" viewBox="0 0 ' + totalWidth + ' ' + totalHeight + '" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">' +
    '<defs>' +
    '<pattern id="' + frameTopPatternId + '" patternUnits="userSpaceOnUse" x="0" y="0" width="' + totalWidth + '" height="' + frameThickness + '">' +
    '<image href="' + frameImageTB + '" x="0" y="0" width="' + totalWidth + '" height="' + frameThickness + '" preserveAspectRatio="none" transform="translate(0 ' + frameThickness + ') scale(1 -1)" />' +
    '</pattern>' +
    '<pattern id="' + frameRightPatternId + '" patternUnits="userSpaceOnUse" x="' + (totalWidth - frameThickness) + '" y="0" width="' + frameThickness + '" height="' + totalHeight + '">' +
    '<image href="' + frameImageLR + '" x="0" y="0" width="' + frameThickness + '" height="' + totalHeight + '" preserveAspectRatio="none" />' +
    '</pattern>' +
    '<pattern id="' + frameBottomPatternId + '" patternUnits="userSpaceOnUse" x="0" y="' + (totalHeight - frameThickness) + '" width="' + totalWidth + '" height="' + frameThickness + '">' +
    '<image href="' + frameImageTB + '" x="0" y="0" width="' + totalWidth + '" height="' + frameThickness + '" preserveAspectRatio="none" />' +
    '</pattern>' +
    '<pattern id="' + frameLeftPatternId + '" patternUnits="userSpaceOnUse" x="0" y="0" width="' + frameThickness + '" height="' + totalHeight + '">' +
    '<image href="' + frameImageLR + '" x="0" y="0" width="' + frameThickness + '" height="' + totalHeight + '" preserveAspectRatio="none" transform="translate(' + frameThickness + ' 0) scale(-1 1)" />' +
    '</pattern>' +
    '<mask id="' + maskRightId + '">' +
    '<path fill="white" d="M ' + (totalWidth - frameThickness) + ',' + frameThickness + ' L ' + totalWidth + ',0 L ' + totalWidth + ',' + totalHeight + ' L ' + (totalWidth - frameThickness) + ',' + (totalHeight - frameThickness) + ' Z" />' +
    '</mask>' +
    '<mask id="' + maskBottomId + '">' +
    '<path fill="white" d="M ' + frameThickness + ',' + (totalHeight - frameThickness) + ' L 0,' + totalHeight + ' L ' + totalWidth + ',' + totalHeight + ' L ' + (totalWidth - frameThickness) + ',' + (totalHeight - frameThickness) + ' Z" />' +
    '</mask>' +
    '<mask id="' + maskLeftId + '">' +
    '<path fill="white" d="M ' + frameThickness + ',' + frameThickness + ' L 0,0 L 0,' + totalHeight + ' L ' + frameThickness + ',' + (totalHeight - frameThickness) + ' Z" />' +
    '</mask>' +
    matPattern +
    '</defs>' +
    '<rect x="0" y="0" width="' + totalWidth + '" height="' + totalHeight + '" fill="#f0f0f0"/>' +
    matPath +
    '<image x="' + imageX + '" y="' + imageY + '" width="' + imageWidth + '" height="' + imageHeight + '"' +
    ' href="' + customerImageUrl + '" preserveAspectRatio="xMidYMid slice"' + rotationAttr + '/>' +
    '<rect x="' + (imageX - 2) + '" y="' + (imageY - 2) + '" width="' + (imageWidth + 4) + '" height="' + (imageHeight + 4) + '"' +
    ' fill="none" stroke="#ccc" stroke-width="2"/>' +
    '<g class="frame">' +
    '<rect x="0" y="0" width="' + totalWidth + '" height="' + frameThickness + '" fill="url(#' + frameTopPatternId + ')" />' +
    '<rect x="' + (totalWidth - frameThickness) + '" y="0" width="' + frameThickness + '" height="' + totalHeight + '" fill="url(#' + frameRightPatternId + ')" mask="url(#' + maskRightId + ')" />' +
    '<rect x="0" y="' + (totalHeight - frameThickness) + '" width="' + totalWidth + '" height="' + frameThickness + '" fill="url(#' + frameBottomPatternId + ')" mask="url(#' + maskBottomId + ')" />' +
    '<rect x="0" y="0" width="' + frameThickness + '" height="' + totalHeight + '" fill="url(#' + frameLeftPatternId + ')" mask="url(#' + maskLeftId + ')" />' +
    '</g>' +
    '</svg>';
}

function escapeXml(unsafe) {
  if (!unsafe) return '';
  return unsafe.replace(/[<>&'"]/g, function(c) {
    switch (c) {
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '&': return '&amp;';
      case "'": return '&apos;';
      case '"': return '&quot;';
      default: return c;
    }
  });
}

/* Expose globally for use by frame-configurator.js */
window.generateFramePreviewSvg = generateFramePreviewSvg;
